import { GameError, type GameEvent } from "./types";

/**
 * Tårnforsvar – inspired by Bloons TD 6. Balloons with layers follow a winding path; you place five kinds of towers, upgrade them,
 * pick their targeting mode and sell them. GOLD comes from popping balloons, from finishing rounds and – most importantly – from
 * answering quiz questions correctly (the quiz is the main income, so learning drives the game).
 *
 * The wave simulation is 100 % deterministic (no randomness) and lives in this shared file: the SERVER runs it to decide the result
 * (gold, lives, pops – so nothing can be cheated), and the CLIENT runs the very same `WaveSim` tick by tick to animate the round.
 */

export const TICKS_PER_SEC = 20;
export const WAVES = 15;
export const MAX_TOWERS = 22;
/** Gold-giving correct answers per round (the quiz keeps flowing afterwards – it then feeds the Hjerne-boost only). */
export const MAX_CORRECT_PER_ROUND = 6;
export const POP_GOLD_PER = 8; // 1 gold per 8 popped layers
export const BOOST_TICKS = 160; // 8 s
export const BOOST_EVERY = 3; // every 3rd correct answer in a row charges the boost
export const MAX_CHARGES = 3;
export const MAX_TICK = 12000;
export const SELL_RATE = 0.7;

import { COLS, ROWS, DEFAULT_MAP, MAPS, MAP_IDS, MAP_DEFS, getMap, isMapId, type MapId, type TdMap } from "./taarnMaps";
export { COLS, ROWS, DEFAULT_MAP, MAPS, MAP_IDS, MAP_DEFS, getMap, isMapId, validateMap, TdMap, type MapId, type MapDef } from "./taarnMaps";

/** The classic map (Skovstien). Kept as module-level helpers for code that does not care about the map. */
const CLASSIC = getMap(DEFAULT_MAP);
export const WAYPOINTS: [number, number][] = CLASSIC.waypoints;
export const PATH_CELLS: [number, number][] = CLASSIC.cells;
export const isPath = (x: number, y: number) => CLASSIC.isPath(x, y);
export const PATH_LENGTH = CLASSIC.length;
export const posAt = (d: number): [number, number] => CLASSIC.posAt(d);

/* ───────────── towers ───────────── */
import { BURN_EVERY, TOWERS, TOWER_TYPES, TARGET_MODES, isTowerType, type TargetMode, type TowerLevel, type TowerType } from "./taarnTowers";
export { BURN_EVERY, TOWERS, TOWER_TYPES, TARGET_MODES, isTowerType, towerUnlockLevel, isTowerUnlocked, type TargetMode, type TowerDef, type TowerLevel, type TowerType } from "./taarnTowers";

export interface Tower {
  id: number;
  x: number;
  y: number;
  type: TowerType;
  level: number; // 1..3
  target: TargetMode;
  spent: number;
}

/* ───────────── difficulty ───────────── */
/** 1 = Let, 2 = Mellem, 3 = Svær. It decides the questions (see the match config) AND how hard the rounds are. */
export type Diff = 1 | 2 | 3;
export const asDiff = (d: unknown): Diff => (d === 1 || d === 3 ? d : 2);
export interface Tune {
  /** how many balloons come (× the classic numbers) */
  count: number;
  /** balloon speed */
  speed: number;
  /** hit points of lead balloons / of bosses */
  lead: number;
  boss: number;
  lives: number;
  cash: number;
  /** how fast the endless rounds after round 15 get harder (× the base growth) */
  grow: number;
  label: string;
  blurb: string;
}
export const TUNE: Record<Diff, Tune> = {
  1: { count: 0.78, speed: 0.93, lead: 0.8, boss: 0.7, lives: 50, cash: 260, grow: 0.75, label: "Let", blurb: "Færre og langsommere balloner, flere liv og lidt mere start-guld." },
  2: { count: 1, speed: 1, lead: 1, boss: 1, lives: 40, cash: 220, grow: 1, label: "Mellem", blurb: "Den klassiske oplevelse." },
  3: { count: 1.6, speed: 1.15, lead: 1.6, boss: 2.2, lives: 30, cash: 180, grow: 1.3, label: "Svær", blurb: "Flere, hurtigere og sejere balloner – og færre liv." },
};

/* ───────────── balloons & waves ───────────── */
/** Layers → speed in cells per second. Lead and boss have their own speeds. */
const LAYER_SPEED = [0, 1.5, 1.9, 2.3, 2.8, 3.4, 3.7, 4.0, 4.3, 4.6];
/** The classic five colours, then the tougher ones that only show up in the endless rounds. */
export const LAYER_COLORS = ["#000", "#ef4444", "#3b82f6", "#22c55e", "#facc15", "#ec4899", "#f97316", "#a855f7", "#14b8a6", "#f8fafc"];
export const MAX_LAYER = LAYER_COLORS.length - 1;
export interface Group {
  layers: number;
  count: number;
  gap: number; // ticks between balloons
  lead?: boolean;
  boss?: boolean;
}
const WAVE_TABLE: Group[][] = [
  [{ layers: 1, count: 12, gap: 10 }],
  [{ layers: 1, count: 18, gap: 7 }],
  [{ layers: 1, count: 10, gap: 7 }, { layers: 2, count: 10, gap: 9 }],
  [{ layers: 2, count: 16, gap: 7 }],
  [{ layers: 2, count: 10, gap: 6 }, { layers: 3, count: 10, gap: 8 }],
  [{ layers: 3, count: 18, gap: 6 }],
  [{ layers: 1, count: 24, gap: 3 }, { layers: 2, count: 12, gap: 5 }, { layers: 3, count: 10, gap: 6 }],
  [{ layers: 3, count: 14, gap: 6 }, { layers: 4, count: 12, gap: 7 }],
  [{ layers: 3, count: 6, gap: 14, lead: true }, { layers: 3, count: 16, gap: 5 }],
  [{ layers: 4, count: 18, gap: 6 }, { layers: 5, count: 8, gap: 6 }],
  [{ layers: 5, count: 18, gap: 5 }, { layers: 3, count: 8, gap: 10, lead: true }],
  [{ layers: 4, count: 26, gap: 4 }, { layers: 12, count: 6, gap: 12, lead: true }, { layers: 5, count: 14, gap: 5 }],
  [{ layers: 12, count: 8, gap: 10, lead: true }, { layers: 5, count: 22, gap: 4 }],
  [{ layers: 5, count: 32, gap: 3 }, { layers: 12, count: 10, gap: 8, lead: true }],
  [{ layers: 140, count: 1, gap: 1, boss: true }, { layers: 5, count: 24, gap: 4 }, { layers: 12, count: 12, gap: 8, lead: true }],
];

export interface Spawn {
  t: number;
  layers: number;
  lead: boolean;
  boss: boolean;
}
/** Extra balloon speed in the endless rounds (+1.2 % per round, at most +35 %). */
export const endlessSpeed = (w: number, diff: Diff = 2) => (w >= WAVES ? 1 + Math.min(0.35, 0.012 * (w - WAVES + 1) * TUNE[diff].grow) : 1);
export const waveSpeedMul = (w: number, diff: Diff = 2) => TUNE[diff].speed * endlessSpeed(w, diff);

/** How much tougher (hit points) the balloons of endless round k are: it compounds, so even a fully upgraded defence breaks in the end. */
export const endlessHp = (k: number, diff: Diff = 2) => 1.085 ** (k * TUNE[diff].grow);

/** The rounds after round 15 ("Spil videre"): every round is a little harder than the one before. k = 0 is round 16. */
export function endlessGroups(k: number, diff: Diff = 2): Group[] {
  const g = TUNE[diff].grow;
  const hp = endlessHp(k, diff);
  const out: Group[] = [];
  // a boss every third round – more of them, and tougher, as the game goes on
  if (k % 3 === 2) out.push({ layers: Math.round((140 + 45 * k * g) * hp), count: 1 + Math.floor((k * g) / 9), gap: 40, boss: true });
  // the fast swarm gets thicker, moves up through the colour tiers (pink → orange → purple → teal → white) and grows armour
  out.push({ layers: Math.round(Math.min(MAX_LAYER, 5 + Math.floor(((k + 1) * g) / 3)) * hp), count: Math.round(Math.min(560, 440 + 10 * k * g)), gap: 4 });
  // lead balloons: only a few kinds of defenders can pop them, and they get a lot of hit points
  out.push({ layers: Math.round((12 + 2.6 * k * g) * hp), count: Math.round(Math.min(260, 230 + 5 * k * g)), gap: k >= 8 ? 6 : 8, lead: true });
  return out;
}

export function waveSpawns(w: number, diff: Diff = 2, map: TdMap | MapId = DEFAULT_MAP): Spawn[] {
  const tune = TUNE[diff];
  const mm = (typeof map === "string" ? getMap(map) : map).def.countMul;
  const speed = waveSpeedMul(w, diff);
  const endless = w >= WAVES;
  const groups = endless ? endlessGroups(w - WAVES, diff) : WAVE_TABLE[Math.max(0, w)]!;
  const out: Spawn[] = [];
  let t = 0;
  for (const gr of groups) {
    let count: number, layers: number;
    if (gr.boss) { count = gr.count; layers = endless ? gr.layers : Math.round(gr.layers * tune.boss); }
    else {
      const grow = endless ? 1 : 1 + 1.4 * w;
      count = Math.max(1, Math.round(gr.count * grow * tune.count * mm));
      layers = gr.lead && !endless ? Math.round(gr.layers * tune.lead) : gr.layers;
      if (gr.lead && endless) layers = Math.round(gr.layers * tune.lead);
    }
    for (let i = 0; i < count; i++) {
      out.push({ t, layers, lead: !!gr.lead, boss: !!gr.boss });
      const sp = (gr.boss ? 0.8 : gr.lead ? 1.4 : LAYER_SPEED[Math.min(MAX_LAYER, Math.max(1, layers))]!) * speed;
      const gap = endless ? gr.gap : Math.round(gr.gap * (1 - 0.02 * w));
      t += Math.max(Math.ceil((0.55 * TICKS_PER_SEC) / sp), gap);
    }
    t += 14;
  }
  return out;
}
export const waveBalloonCount = (w: number, diff: Diff = 2, map: TdMap | MapId = DEFAULT_MAP) => waveSpawns(w, diff, map).length;
/** A short description of a round for the "next round" card. */
export function waveInfo(w: number, diff: Diff = 2, map: TdMap | MapId = DEFAULT_MAP) {
  const sp = waveSpawns(w, diff, map);
  return { balloons: sp.length, boss: sp.some((x) => x.boss), lead: sp.some((x) => x.lead), maxLayers: Math.max(...sp.filter((x) => !x.boss && !x.lead).map((x) => x.layers), 1), endless: w >= WAVES };
}

export interface Balloon {
  id: number;
  d: number; // distance travelled along the path (cells)
  layers: number;
  max: number;
  lead: boolean;
  boss: boolean;
  slowUntil: number;
  slow: number;
  /** burning until this tick (loses a layer every BURN_EVERY ticks) */
  burnUntil: number;
  /** cells still to be blown back along the path */
  push: number;
}
export interface StepResult {
  /** one entry per projectile; `hits` = where the balloons that were hit stood (for chain lightning, beams, splash …) */
  shots: { tid: number; x: number; y: number; bid: number; hits: [number, number][]; crit: boolean }[];
  /** gold a tower earned by itself this tick (Sparegris) */
  income: { tid: number; amount: number }[];
  pops: { x: number; y: number; layers: number; boss: boolean }[];
  leaked: number;
  /** gold earned this tick from popping */
  gold: number;
}

/** Things that can happen DURING a round. `t` = simulation tick at which it is applied (before that tick's step). */
export type WaveEv =
  | { t: number; k: "build"; x: number; y: number; tower: TowerType }
  | { t: number; k: "upgrade"; id: number }
  | { t: number; k: "sell"; id: number }
  | { t: number; k: "target"; id: number; mode: TargetMode }
  | { t: number; k: "cash"; amount: number }
  | { t: number; k: "boost" };
type DistributiveOmit<T, K extends keyof any> = T extends unknown ? Omit<T, K> : never;
export type WaveEvInput = DistributiveOmit<WaveEv, "t">;

interface Board {
  cash: number;
  towers: Tower[];
  nextId: number;
}
function findTower(b: Board, id: unknown): Tower {
  const t = b.towers.find((x) => x.id === id);
  if (!t) throw new GameError("Der er intet tårn her.");
  return t;
}
function doBuild(b: Board, map: TdMap, x: unknown, y: unknown, tower: unknown): GameEvent[] {
  if (!Number.isInteger(x) || !Number.isInteger(y) || (x as number) < 0 || (y as number) < 0 || (x as number) >= COLS || (y as number) >= ROWS) throw new GameError("Ugyldigt felt.");
  if (!isTowerType(tower)) throw new GameError("Ukendt tårntype.");
  const cx = x as number, cy = y as number, type = tower as TowerType;
  if (map.isPath(cx, cy)) throw new GameError("Du kan ikke bygge på stien.");
  if (b.towers.some((t) => t.x === cx && t.y === cy)) throw new GameError("Der står allerede et tårn her.");
  if (b.towers.length >= MAX_TOWERS) throw new GameError(`Du kan højst have ${MAX_TOWERS} tårne.`);
  const def = TOWERS[type];
  if (b.cash < def.cost) throw new GameError(`Du mangler guld (kræver ${def.cost}).`);
  b.cash -= def.cost;
  const t: Tower = { id: b.nextId++, x: cx, y: cy, type, level: 1, target: def.defaultTarget, spent: def.cost };
  b.towers.push(t);
  return [{ kind: "build", data: { id: t.id, x: cx, y: cy, tower: type } }];
}
function doUpgrade(b: Board, id: unknown): GameEvent[] {
  const t = findTower(b, id);
  if (t.level >= 3) throw new GameError("Tårnet er allerede på højeste niveau.");
  const cost = TOWERS[t.type].upgrades[t.level - 1]!;
  if (b.cash < cost) throw new GameError(`Du mangler guld (kræver ${cost}).`);
  b.cash -= cost;
  t.spent += cost;
  t.level++;
  return [{ kind: "upgrade", data: { id: t.id, level: t.level } }];
}
function doSell(b: Board, id: unknown): GameEvent[] {
  const t = findTower(b, id);
  const v = towerSellValue(t);
  b.cash += v;
  b.towers = b.towers.filter((x) => x.id !== t.id);
  return [{ kind: "sell", data: { id: t.id, value: v } }];
}
function doTarget(b: Board, id: unknown, mode: unknown): GameEvent[] {
  const t = findTower(b, id);
  if (typeof mode !== "string" || !TARGET_MODES.includes(mode as TargetMode)) throw new GameError("Ukendt målvalg.");
  t.target = mode as TargetMode;
  return [];
}

/** Deterministic wave simulation, stepped one tick at a time. Also used LIVE: towers can be built/upgraded/sold during the round. */
export class WaveSim implements Board {
  tick = 0;
  balloons: Balloon[] = [];
  popsTotal = 0;
  livesLost = 0;
  leakedCount = 0;
  /** gold earned from popping during this round */
  earned = 0;
  cash: number;
  nextId: number;
  towers: Tower[];
  boostUntil = 0;
  defeated = false;
  /** how many events of the shared event list were applied (see runTo) */
  evPtr = 0;
  /** the map and difficulty this round is played on */
  readonly map: TdMap;
  readonly difficulty: Diff;
  private readonly speedMul: number;
  private maxLives: number | null;
  private queue: Spawn[];
  private next = 0;
  private cd = new Map<number, number>();
  /** shots fired per tower (for every-n-th-shot critical hits) */
  private shotN = new Map<number, number>();
  constructor(towers: Tower[], public wave: number, opts: { cash?: number; nextId?: number; lives?: number; map?: MapId | TdMap; difficulty?: number } = {}) {
    this.towers = towers.map((t) => ({ ...t })).sort((a, b) => a.id - b.id);
    this.cash = opts.cash ?? 0;
    this.nextId = opts.nextId ?? Math.max(0, ...towers.map((t) => t.id)) + 1;
    this.maxLives = opts.lives ?? null;
    this.map = typeof opts.map === "object" ? opts.map : getMap(opts.map);
    this.difficulty = asDiff(opts.difficulty);
    this.speedMul = waveSpeedMul(wave, this.difficulty);
    this.queue = waveSpawns(wave, this.difficulty, this.map);
    for (const t of this.towers) this.cd.set(t.id, this.firstCd(t));
  }
  /** towers that earn money wait one full period before the first payout (no instant income by building them) */
  private firstCd(t: Tower) {
    const lv = TOWERS[t.type].levels[t.level - 1]!;
    return lv.income ? lv.cd : 0;
  }
  done() {
    return this.defeated || (this.next >= this.queue.length && this.balloons.length === 0);
  }
  get boosting() {
    return this.tick < this.boostUntil;
  }
  /** balloons that have not rolled out onto the track yet */
  get waiting() {
    return this.queue.length - this.next;
  }
  /** all balloons this round will send */
  get planned() {
    return this.queue.length;
  }
  speedOf(b: Balloon) {
    const base = b.boss ? 0.8 : b.lead ? 1.4 : LAYER_SPEED[Math.min(MAX_LAYER, Math.max(1, b.layers))]!;
    return ((base * this.speedMul) / TICKS_PER_SEC) * (this.tick < b.slowUntil ? b.slow : 1);
  }
  /** Apply one in-round event NOW (throws GameError when illegal). */
  apply(ev: WaveEvInput): GameEvent[] {
    switch (ev.k) {
      case "build": {
        const e = doBuild(this, this.map, ev.x, ev.y, ev.tower);
        this.cd.set(this.nextId - 1, this.firstCd(this.towers[this.towers.length - 1]!));
        return e;
      }
      case "upgrade": return doUpgrade(this, ev.id);
      case "sell": {
        const e = doSell(this, ev.id);
        this.cd.delete(ev.id);
        return e;
      }
      case "target": return doTarget(this, ev.id, ev.mode);
      case "cash":
        if (!Number.isFinite(ev.amount) || ev.amount <= 0 || ev.amount > 1000) throw new GameError("Ugyldigt beløb.");
        this.cash += Math.floor(ev.amount);
        return [];
      case "boost":
        if (this.boosting) throw new GameError("Hjerne-boost er allerede aktiv.");
        this.boostUntil = this.tick + BOOST_TICKS;
        return [{ kind: "boost" }];
      default: throw new GameError("Ukendt handling.");
    }
  }
  /** Replay: apply the events due so far and step until `tick` (events at tick T are applied before the step of tick T). */
  runTo(events: WaveEv[], tick: number) {
    for (;;) {
      while (this.evPtr < events.length && events[this.evPtr]!.t <= this.tick) {
        try {
          this.apply(events[this.evPtr]!);
        } catch {
          /* an event that was valid when recorded; never brick a replay */
        }
        this.evPtr++;
      }
      if (this.tick >= tick || this.done()) break;
      this.step();
    }
    // the round is over: money earned from answers that arrived after the last tick still counts
    if (this.done()) {
      for (; this.evPtr < events.length; this.evPtr++) {
        const e = events[this.evPtr]!;
        if (e.k === "cash") this.apply(e);
      }
    }
  }
  /** fire-rate bonus of every tower from nearby support towers (Trommepanda), capped at +100 % */
  private buffMap(): Map<number, number> {
    const out = new Map<number, number>();
    for (const a of this.towers) {
      const lv = TOWERS[a.type].levels[a.level - 1]!;
      if (!lv.buff) continue;
      for (const t of this.towers) {
        if (t === a) continue;
        const d = Math.hypot(t.x - a.x, t.y - a.y);
        if (d <= lv.range) out.set(t.id, Math.min(1, (out.get(t.id) ?? 0) + lv.buff));
      }
    }
    return out;
  }
  step(): StepResult {
    const res: StepResult = { shots: [], income: [], pops: [], leaked: 0, gold: 0 };
    // spawn
    while (this.next < this.queue.length && this.queue[this.next]!.t <= this.tick) {
      const s = this.queue[this.next]!;
      this.balloons.push({ id: this.next, d: 0, layers: s.layers, max: s.layers, lead: s.lead, boss: s.boss, slowUntil: 0, slow: 1, burnUntil: 0, push: 0 });
      this.next++;
    }
    const boost = this.boosting ? 2 : 1;
    const buffs = this.buffMap();
    // towers
    for (const tw of this.towers) {
      const def = TOWERS[tw.type];
      const lv = def.levels[tw.level - 1]!;
      const left = (this.cd.get(tw.id) ?? 0) - boost * (1 + (buffs.get(tw.id) ?? 0));
      if (left > 0) {
        this.cd.set(tw.id, left);
        continue;
      }
      if (lv.income) { // a money tower: pays out, does not shoot
        this.cd.set(tw.id, lv.cd);
        this.cash += lv.income;
        this.earned += lv.income;
        res.income.push({ tid: tw.id, amount: lv.income });
        continue;
      }
      if (lv.buff) { // a support tower: only boosts the others
        this.cd.set(tw.id, 1);
        continue;
      }
      const tx = tw.x + 0.5, ty = tw.y + 0.5;
      const inRange: { b: Balloon; x: number; y: number; dist: number }[] = [];
      for (const b of this.balloons) {
        if (b.lead && !lv.hitsLead) continue;
        const [bx, by] = this.map.posAt(b.d);
        const dist = Math.hypot(bx - tx, by - ty);
        if (dist <= lv.range) inRange.push({ b, x: bx, y: by, dist });
      }
      if (!inRange.length) {
        this.cd.set(tw.id, 1); // check again next tick
        continue;
      }
      inRange.sort((a, c) => {
        switch (tw.target) {
          case "last": return a.b.d - c.b.d || a.b.id - c.b.id;
          case "strong": return c.b.layers - a.b.layers || c.b.d - a.b.d || a.b.id - c.b.id;
          case "close": return a.dist - c.dist || a.b.id - c.b.id;
          default: return c.b.d - a.b.d || a.b.id - c.b.id;
        }
      });
      this.cd.set(tw.id, lv.cd);
      const n = (this.shotN.get(tw.id) ?? 0) + 1;
      this.shotN.set(tw.id, n);
      const crit = !!lv.critEvery && n % lv.critEvery === 0;
      const near = (c: { x: number; y: number }, r: number, skip: unknown) =>
        inRange.filter((o) => o !== skip && Math.hypot(o.x - c.x, o.y - c.y) <= r).sort((a, d) => Math.hypot(a.x - c.x, a.y - c.y) - Math.hypot(d.x - c.x, d.y - c.y) || a.b.id - d.b.id);
      // which balloons does each projectile hit?
      const volleys: { main: (typeof inRange)[number]; victims: (typeof inRange)[number][] }[] = [];
      if (lv.aura) {
        // frost / rain / wind: every balloon in range (up to `multi`)
        volleys.push({ main: inRange[0]!, victims: inRange.slice(0, lv.multi ?? 99) });
      } else {
        const picks = inRange.slice(0, Math.max(1, lv.multi ?? 1));
        for (const main of picks) {
          if (lv.splash > 0) volleys.push({ main, victims: inRange.filter((c) => Math.hypot(c.x - main.x, c.y - main.y) <= lv.splash) });
          else volleys.push({ main, victims: [main, ...near(main, lv.link ?? 1.1, main).slice(0, Math.max(0, lv.pierce - 1))] });
        }
      }
      for (const vo of volleys) {
        res.shots.push({ tid: tw.id, x: vo.main.x, y: vo.main.y, bid: vo.main.b.id, hits: vo.victims.slice(0, 16).map((c) => [c.x, c.y] as [number, number]), crit });
        for (const c of vo.victims) {
          const b = c.b;
          if (lv.freeze > 0) {
            b.slow = this.tick < b.slowUntil ? Math.min(b.slow, lv.slow) : lv.slow;
            b.slowUntil = this.tick + lv.freeze;
          }
          if (lv.burn) b.burnUntil = Math.max(b.burnUntil, this.tick + lv.burn);
          if (lv.knock) b.push = Math.min(3, b.push + lv.knock);
          if (lv.dmg > 0) this.hit(b, lv.dmg * (crit ? lv.critMul ?? 1 : 1) * (b.boss ? lv.bossMul ?? 1 : 1), res);
        }
      }
    }
    // burning balloons lose a layer now and then
    if (this.tick % BURN_EVERY === 0) for (const b of this.balloons) if (b.burnUntil > this.tick) this.hit(b, 1, res);
    this.balloons = this.balloons.filter((b) => b.layers > 0);
    // move
    for (const b of this.balloons) {
      if (b.push > 0) { // blown back (spread over a few ticks so it reads as a gust, not a teleport)
        const back = Math.min(b.push, 0.25);
        b.push -= back;
        b.d = Math.max(0, b.d - back);
      }
      b.d += this.speedOf(b);
      if (b.d >= this.map.length) {
        const lost = b.boss ? Math.min(20, b.layers) : b.layers;
        this.livesLost += lost;
        this.leakedCount++;
        res.leaked += lost;
        b.layers = 0;
      }
    }
    if (this.maxLives !== null && this.livesLost >= this.maxLives) this.defeated = true;
    this.balloons = this.balloons.filter((b) => b.layers > 0);
    this.tick++;
    return res;
  }
  private hit(b: Balloon, dmg: number, res: StepResult) {
    if (dmg <= 0 || b.layers <= 0) return;
    const taken = Math.min(b.layers, dmg);
    b.layers -= taken;
    const before = Math.floor(this.popsTotal / POP_GOLD_PER);
    this.popsTotal += taken;
    const g = Math.floor(this.popsTotal / POP_GOLD_PER) - before;
    if (g > 0) {
      this.cash += g;
      this.earned += g;
      res.gold += g;
    }
    const [x, y] = this.map.posAt(b.d);
    res.pops.push({ x, y, layers: taken, boss: b.boss });
  }
}

export function runWave(towers: Tower[], wave: number, opts: { cash?: number; lives?: number; map?: MapId | TdMap; difficulty?: number } = {}) {
  const sim = new WaveSim(towers, wave, opts);
  let guard = 0;
  while (!sim.done() && guard++ < MAX_TICK) sim.step();
  return { pops: sim.popsTotal, livesLost: sim.livesLost, leaked: sim.leakedCount, ticks: sim.tick, earned: sim.earned, sim };
}

/* ───────────── game state ───────────── */
export interface WaveRun {
  index: number;
  /** snapshot at the moment the round started */
  towers: Tower[];
  cash: number;
  nextId: number;
  events: WaveEv[];
  startedAt: number;
}
export interface TaarnState {
  /** "victory" = round 15 is cleared: the player chooses "Spil videre" (endless rounds) or "Afslut" */
  phase: "build" | "victory" | "over";
  mapId: MapId;
  /** 1 = Let, 2 = Mellem, 3 = Svær – decides both the questions and how tough the rounds are */
  difficulty: Diff;
  /** true once the player has pressed "Spil videre" – the rounds after round 15 get harder and harder */
  endless: boolean;
  wave: number; // index of the NEXT round (0-based)
  lives: number;
  maxLives: number;
  cash: number;
  towers: Tower[];
  nextId: number;
  /** correct answers this round (the first MAX_CORRECT_PER_ROUND give gold) */
  correctThisRound: number;
  pops: number;
  leaks: number;
  won: boolean | null;
  /** current streak of correct answers and stored Hjerne-boost charges */
  streak: number;
  charges: number;
  /** a round that is running right now (null = build phase) */
  run: WaveRun | null;
  lastWave: { index: number; towers: Tower[]; pops: number; livesLost: number; leaked: number; cash: number; bonus: number } | null;
}
export type TaarnAction =
  | { type: "ask" }
  | { type: "build"; x: number; y: number; tower: TowerType }
  | { type: "upgrade"; id: number }
  | { type: "sell"; id: number }
  | { type: "target"; id: number; mode: TargetMode }
  | { type: "startWave" } // instant round (no live play)
  | { type: "startRun" }
  | { type: "finishWave" }
  | { type: "boost" }
  /** after round 15: keep playing (endless, ever harder rounds) */
  | { type: "continue" }
  /** after round 15 / while playing endless: stop here and bank the result */
  | { type: "finish" };

export function createTaarn(opts: { grade: number; difficulty: number; map?: MapId }): TaarnState {
  const difficulty = asDiff(opts.difficulty);
  const { lives, cash } = TUNE[difficulty];
  return { phase: "build", mapId: isMapId(opts.map) ? opts.map : DEFAULT_MAP, difficulty, endless: false, wave: 0, lives, maxLives: lives, cash, towers: [], nextId: 1, correctThisRound: 0, pops: 0, leaks: 0, won: null, streak: 0, charges: 0, run: null, lastWave: null };
}
/** Older saved games lack the newer fields. */
export function normalizeTaarn(s: TaarnState): TaarnState {
  s.streak ??= 0;
  s.charges ??= 0;
  s.run ??= null;
  if (!isMapId(s.mapId)) s.mapId = DEFAULT_MAP;
  s.difficulty = asDiff(s.difficulty);
  s.endless ??= false;
  return s;
}
/** Gold for the "Spil videre" button – a little treat so the first endless round can be prepared. */
export const CONTINUE_BONUS = 150;
/** Extra score for every endless round survived. */
export const ENDLESS_SCORE = 30;

export const goldForAnswer = (difficulty: number) => 35 + 15 * difficulty;
export const roundBonus = (wave: number) => 30 + 4 * wave;
export const streakBonus = (streak: number) => Math.min(5, Math.max(0, streak - 1)) * 5;

/**
 * A quiz answer. Correct: gold (only the first MAX_CORRECT_PER_ROUND each round) + a streak bonus; every 3rd correct answer in a row
 * charges a Hjerne-boost. During a live round the gold becomes a `cash` event at `tick` so that the replay stays deterministic.
 */
export function onAnswer(s: TaarnState, correct: boolean, difficulty: number, tick = 0): GameEvent[] {
  normalizeTaarn(s);
  if (!correct) {
    s.streak = 0;
    return [{ kind: "no-gold" }];
  }
  s.streak++;
  s.correctThisRound++;
  const gold = s.correctThisRound <= MAX_CORRECT_PER_ROUND ? goldForAnswer(difficulty) + streakBonus(s.streak) : 0;
  let charge = false;
  if (s.streak % BOOST_EVERY === 0 && s.charges < MAX_CHARGES) {
    s.charges++;
    charge = true;
  }
  if (gold > 0) {
    if (s.run) {
      const last = s.run.events.length ? s.run.events[s.run.events.length - 1]!.t : 0;
      s.run.events.push({ t: Math.max(last, Math.max(0, Math.floor(tick))), k: "cash", amount: gold });
    } else s.cash += gold;
  }
  return [{ kind: "gold", data: { amount: gold, streak: s.streak, charge } }];
}

export function canAsk(s: TaarnState): string | null {
  if (s.phase === "victory") return "Du har klaret alle 15 runder! Vælg «Spil videre» eller «Afslut».";
  if (s.phase !== "build") return "Spillet er slut.";
  return null;
}

export function towerSellValue(t: Tower) {
  return Math.floor(t.spent * SELL_RATE);
}

/* ───────────── live rounds ───────────── */
export function startRun(s: TaarnState, now: number): GameEvent[] {
  normalizeTaarn(s);
  if (s.phase === "victory") throw new GameError("Du har klaret alle 15 runder! Vælg «Spil videre» eller «Afslut».");
  if (s.phase !== "build") throw new GameError("Spillet er slut.");
  if (s.run) throw new GameError("Runden er allerede i gang.");
  if (s.towers.length === 0) throw new GameError("Køb mindst ét tårn, før du starter runden.");
  s.run = { index: s.wave, towers: s.towers.map((t) => ({ ...t })), cash: s.cash, nextId: s.nextId, events: [], startedAt: now };
  return [{ kind: "start", data: { wave: s.wave + 1 } }];
}

/** The simulation of the running round, advanced to `tick` with every recorded event applied. */
export function simOfRun(s: TaarnState, tick: number): WaveSim {
  const r = s.run!;
  const sim = new WaveSim(r.towers, r.index, { cash: r.cash, nextId: r.nextId, lives: s.lives, map: s.mapId, difficulty: s.difficulty });
  sim.runTo(r.events, tick);
  return sim;
}

/** Record + validate one player action during a live round. */
export function recordRunEvent(s: TaarnState, input: WaveEvInput, tick: unknown): GameEvent[] {
  const r = s.run;
  if (!r) throw new GameError("Der kører ingen runde.");
  if (!Number.isInteger(tick) || (tick as number) < 0 || (tick as number) > MAX_TICK) throw new GameError("Ugyldigt tidspunkt.");
  const last = r.events.length ? r.events[r.events.length - 1]!.t : 0;
  const t = Math.max(last, tick as number);
  const sim = simOfRun(s, t);
  if (sim.done() && sim.tick < t) throw new GameError("Runden er slut.");
  if (input.k === "boost") {
    if (s.charges <= 0) throw new GameError("Du har ingen Hjerne-boost. Svar rigtigt tre gange i træk for at lade op.");
    const ev = sim.apply(input);
    s.charges--;
    r.events.push({ ...input, t } as WaveEv);
    return ev;
  }
  const ev = sim.apply(input);
  r.events.push({ ...input, t } as WaveEv);
  return ev;
}

/** Simulate the rest of the running round and settle it. */
export function finishRun(s: TaarnState): GameEvent[] {
  normalizeTaarn(s);
  const r = s.run;
  if (!r) throw new GameError("Der kører ingen runde.");
  const sim = new WaveSim(r.towers, r.index, { cash: r.cash, nextId: r.nextId, lives: s.lives, map: s.mapId, difficulty: s.difficulty });
  sim.runTo(r.events, MAX_TICK);
  s.run = null;
  s.towers = sim.towers.map((t) => ({ ...t }));
  s.nextId = sim.nextId;
  s.cash = sim.cash;
  s.pops += sim.popsTotal;
  s.leaks += sim.leakedCount;
  s.lives = Math.max(0, s.lives - sim.livesLost);
  const index = r.index;
  s.wave++;
  s.correctThisRound = 0;
  const bonus = s.lives > 0 ? roundBonus(index) : 0;
  s.cash += bonus;
  s.lastWave = { index, towers: s.towers.map((t) => ({ ...t })), pops: sim.popsTotal, livesLost: sim.livesLost, leaked: sim.leakedCount, cash: sim.earned, bonus };
  const ev: GameEvent[] = [{ kind: "wave", data: { wave: s.wave, pops: sim.popsTotal, leaked: sim.leakedCount } }];
  if (s.lives <= 0) {
    s.phase = "over";
    // whoever got through the 15 rounds has won, even if the endless rounds were what finally broke the defence
    s.won = s.endless;
    ev.push({ kind: "lose" });
  } else if (s.wave >= WAVES && !s.endless) {
    s.phase = "victory"; // the choice is theirs: keep going or bank the win
    s.won = true;
    ev.push({ kind: "win" });
  }
  return ev;
}

export function applyTaarn(s: TaarnState, a: TaarnAction): GameEvent[] {
  normalizeTaarn(s);
  if (s.phase === "over") throw new GameError("Spillet er slut.");
  if (s.run && a.type !== "ask") throw new GameError("Runden kører – brug live-handlinger.");
  if (s.phase === "victory" && a.type !== "continue" && a.type !== "finish") throw new GameError("Du har klaret alle 15 runder! Vælg «Spil videre» eller «Afslut».");
  switch (a.type) {
    case "continue": {
      if (s.phase !== "victory") throw new GameError("Du kan først spille videre, når du har klaret runde 15.");
      s.phase = "build";
      s.endless = true;
      s.cash += CONTINUE_BONUS;
      s.correctThisRound = 0;
      return [{ kind: "continue", data: { wave: s.wave + 1, bonus: CONTINUE_BONUS } }];
    }
    case "finish": {
      if (s.phase !== "victory" && !(s.endless && s.phase === "build")) throw new GameError("Du kan først afslutte, når du har klaret runde 15.");
      s.phase = "over";
      s.won = true;
      return [{ kind: "finish", data: { wave: s.wave } }];
    }
    case "build": return doBuild(s, getMap(s.mapId), a.x, a.y, a.tower);
    case "upgrade": return doUpgrade(s, a.id);
    case "sell": return doSell(s, a.id);
    case "target": return doTarget(s, a.id, a.mode);
    case "startWave": {
      startRun(s, 0); // instant round: no live events
      return finishRun(s);
    }
    case "boost": throw new GameError("Hjerne-boost kan kun bruges, mens en runde kører.");
    case "ask":
      throw new GameError("Spørgsmål håndteres af motoren.");
    default:
      throw new GameError("Ukendt handling.");
  }
}

export function scoreTaarn(s: TaarnState): number {
  return s.pops * 2 + s.lives * 10 + s.wave * 20 + (s.won ? 300 : 0) + Math.max(0, s.wave - WAVES) * ENDLESS_SCORE;
}
